import { supabase } from "@/integrations/supabase/client";
import type { BulkRosterWarning } from "@/lib/bulkRosterWarnings";

export type BulkRosterAddExisting = {
  runId: string;
  celebrationId: string;
  confirmWarnings?: boolean;
};

export type BulkRosterAddNew = {
  runId: string;
  name: string;
  email?: string;
  birthday?: string;
  startDate?: string;
  occasionType: string;
  celebrationDate: string;
  dietaryRequirements?: string[];
  confirmWarnings?: boolean;
};

type ApiOk = {
  ok: true;
  celebration_id: string;
  team_member_id: string;
};

type ApiWarn = {
  ok: false;
  requires_confirmation?: boolean;
  blocked?: boolean;
  warnings?: BulkRosterWarning[];
  error?: string;
};

async function invoke(body: Record<string, unknown>): Promise<ApiOk> {
  const { data, error } = await supabase.functions.invoke("bulk-roster-add", { body });
  if (error) throw new Error(error.message);
  const payload = data as ApiWarn | ApiOk;
  if (payload && "ok" in payload && payload.ok) {
    return payload as ApiOk;
  }
  const fail = payload as ApiWarn;
  if (fail.requires_confirmation && fail.warnings?.length) {
    const err = new Error("ROSTER_WARNINGS");
    (err as Error & { warnings: BulkRosterWarning[] }).warnings = fail.warnings;
    throw err;
  }
  throw new Error(fail.error ?? "Couldn't update roster");
}

export async function addExistingToBulkRoster(args: BulkRosterAddExisting): Promise<ApiOk> {
  return invoke({
    mode: "existing",
    run_id: args.runId,
    celebration_id: args.celebrationId,
    confirm_warnings: args.confirmWarnings ?? false,
  });
}

export async function addNewMemberToBulkRoster(args: BulkRosterAddNew): Promise<ApiOk> {
  return invoke({
    mode: "new",
    run_id: args.runId,
    name: args.name,
    email: args.email,
    birthday: args.birthday,
    start_date: args.startDate,
    occasion_type: args.occasionType,
    celebration_date: args.celebrationDate,
    dietary_requirements: args.dietaryRequirements,
    confirm_warnings: args.confirmWarnings ?? false,
  });
}

export function isRosterWarningsError(
  err: unknown,
): err is Error & { warnings: BulkRosterWarning[] } {
  return err instanceof Error && err.message === "ROSTER_WARNINGS" && "warnings" in err;
}
